import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createPartner } from "api";
import { PageHeader, FormField, Spinner, Select } from "components/common";
import type { PartnerType } from "types";

interface FormState {
  name: string;
  type: PartnerType | "";
}

const INITIAL: FormState = { name: "", type: "" };

export default function NewPartnerForm() {
  const navigate     = useNavigate();
  const queryClient  = useQueryClient();
  const [form, setForm] = useState<FormState>(INITIAL);

  const set = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => createPartner({ name: form.name, type: form.type as PartnerType }),
    onSuccess: (partner) => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      navigate("/partners/" + partner.id);
    },
  });

  const isValid = form.name.trim() && form.type;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) mutate();
  };

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-lg">
      <button
        onClick={() => navigate("/add-new")}
        className="text-sm text-gray-400 hover:text-gray-600 mb-6 transition-colors"
      >
        ← Back
      </button>

      <PageHeader title="Add new partner" subtitle="Register a donator, distributor, or ops partner." />

      <form onSubmit={handleSubmit} className="card p-6 space-y-5">
        <FormField label="Organisation name">
          <input
            className="input"
            value={form.name}
            onChange={set("name")}
            placeholder="e.g. City Food Bank"
            required
            autoFocus
          />
        </FormField>

        <FormField label="Type">
          <Select value={form.type} onChange={set("type")}>
            <option value="" disabled>Select a type…</option>
            <option value="donator">Donator</option>
            <option value="distributor">Distributor</option>
            <option value="ops">Ops</option>
          </Select>
        </FormField>

        {error && (
          <p className="text-sm text-red-500">Something went wrong. Please try again.</p>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate("/add-new")}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!isValid || isPending}>
            {isPending ? <Spinner className="h-4" /> : "Create partner"}
          </button>
        </div>
      </form>
    </div>
  );
}